import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Seo from '../components/Seo';
import { BackToTop } from '../components/ChatAndUtils';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { CheckCircle2, ShieldCheck, XCircle } from 'lucide-react';
import { Spinner, eur } from '../components/RatenShared';
import { ZahlartWahl } from '../components/zahlung/ZahlartWahl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Zeile = ({ k, v, mono }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-gray-100 last:border-0 text-sm"><span className="text-[#7a8a99]">{k}</span><span className={`text-[#1B3A5C] text-right ${mono ? 'font-mono text-xs' : 'font-medium'}`}>{v}</span></div>
);

export default function AkontoAngebot() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { akontoId } = useParams();
  const [params] = useSearchParams();
  const key = params.get('key') || '';
  const cancelled = params.get('cancelled') === '1';
  const [doc, setDoc] = useState(null);
  const [fehler, setFehler] = useState('');
  const [datenschutz, setDatenschutz] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/akonto/angebot/${akontoId}`, { params: { key } }).then((r) => setDoc(r.data)).catch((e) => setFehler(e?.response?.data?.detail || t('akonto.angebot.link_ungueltig')));
  }, [akontoId, key, t]);

  const zahlen = async (zahlart) => {
    if (!datenschutz) { toast({ title: t('raten.form.fehler_datenschutz'), variant: 'destructive' }); return; }
    setBusy(zahlart);
    try {
      const r = await axios.post(`${API}/akonto/angebot/${akontoId}/checkout`, { key, origin_url: window.location.origin, datenschutz, zahlart });
      window.location.href = r.data.checkout_url;
    } catch (err) { toast({ title: t('akonto.form.fehler_titel'), description: err?.response?.data?.detail || '', variant: 'destructive' }); setBusy(false); }
  };

  const bezahlt = doc?.status === 'paid';
  return (
    <div className="min-h-screen bg-[#f5f8fa]">
      <Seo title={`${t('akonto.angebot.titel')} – Kanzlei Nabhan Gäbler & Partner`} description={t('akonto.seo_desc')} path={`/akontozahlung/${akontoId}`} noindex />
      <Header />
      <main className="pt-[150px] pb-20">
        <section className="bg-[#1B3A5C] text-white py-14">
          <div className="max-w-3xl mx-auto px-6 lg:px-10">
            <div className="flex items-center gap-3 text-[11px] tracking-[0.3em] text-[#e0b56a] uppercase mb-3"><span className="w-10 h-px bg-[#e0b56a]"></span>{t('akonto.kicker')}</div>
            <h1 className="font-serif text-4xl sm:text-5xl font-light" data-testid="akonto-angebot-title">{t('akonto.angebot.titel')}</h1>
            <p className="text-white/75 mt-4 text-sm sm:text-base">{t('akonto.angebot.untertitel')}</p>
          </div>
        </section>
        <section className="max-w-3xl mx-auto px-6 lg:px-10 -mt-8">
          {fehler ? (
            <div className="bg-white rounded-sm shadow-sm p-8 text-center" data-testid="akonto-angebot-fehler"><XCircle className="w-12 h-12 text-rose-600 mx-auto mb-3" /><p className="text-sm text-[#34536b]">{fehler}</p><Link to="/akontozahlung" className="inline-block mt-5 text-xs tracking-wider text-[#b8893d] hover:underline">{t('akonto.status.nochmal')}</Link></div>
          ) : !doc ? <div className="bg-white rounded-sm shadow-sm p-8 flex justify-center"><Spinner /></div> : (
            <div className="bg-white rounded-sm shadow-sm p-6 sm:p-8" data-testid="akonto-angebot-card">
              {cancelled && !bezahlt && <div className="mb-5 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-sm px-4 py-3" data-testid="akonto-angebot-cancelled">{t('akonto.angebot.abgebrochen')}</div>}
              {bezahlt && <div className="mb-5 flex items-center gap-2 text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-sm px-4 py-3" data-testid="akonto-angebot-bezahlt"><CheckCircle2 className="w-4 h-4" /> {t('akonto.angebot.bereits_bezahlt')}</div>}
              <p className="text-sm text-[#34536b]">{t('akonto.angebot.anrede', { name: doc.mandant?.name })}</p>
              <div className="font-serif text-5xl text-[#1B3A5C] mt-4" data-testid="akonto-angebot-betrag">{eur(doc.betrag)}</div>
              <div className="text-xs text-[#7a8a99] mt-1">{doc.verwendungszweck}</div>
              <div className="mt-6">
                <Zeile k={t('akonto.form.verwendungszweck_kurz')} v={doc.verwendungszweck} />
                <Zeile k={t('akonto.angebot.referenz')} v={doc.referenz_text} />
                <Zeile k={t('akonto.angebot.zahlungs_id')} v={doc.akonto_id} mono />
              </div>
              {doc.nachricht && <p className="mt-5 text-sm text-[#34536b] whitespace-pre-line bg-[#f5f8fa] rounded-sm p-4" data-testid="akonto-angebot-nachricht">{doc.nachricht}</p>}
              {!bezahlt && (
                <>
                  <label className="flex items-start gap-3 text-xs text-[#34536b] cursor-pointer mt-6"><input type="checkbox" className="mt-0.5 w-4 h-4 accent-[#b8893d]" checked={datenschutz} onChange={(e) => setDatenschutz(e.target.checked)} data-testid="akonto-angebot-datenschutz" /><span>{t('raten.form.datenschutz_vor')} <Link to="/datenschutz" target="_blank" className="text-[#b8893d] underline">{t('raten.form.datenschutz_link')}</Link> {t('akonto.form.datenschutz_nach')}</span></label>
                  <div className="mt-6" data-testid="akonto-angebot-pay"><ZahlartWahl betrag={doc.betrag} busy={busy} onWahl={zahlen} testId="akonto-angebot-zahlart" /></div>
                  <p className="text-[11px] text-[#7a8a99] mt-4 flex items-start gap-2"><ShieldCheck className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {t('akonto.form.rechtshinweis')}</p>
                </>
              )}
            </div>
          )}
        </section>
      </main>
      <Footer />
      <BackToTop />
      <Toaster />
    </div>
  );
}
